import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormText,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  getHelpArticleUrl,
  HELP_ARTICLE_CLIENTS,
} from "../../../../helpers/helpCenter";
import { resolveCurrentTenantId } from "../../../../helpers/agendaUnitContext";
import {
  getClientsLastOptionsActionForTenant,
  setClientsLastOptionsActionForTenant,
  type ClientsOptionsActionPreference,
} from "../../../../helpers/clientsListContext";
import AnamnesisPromptModal from "../../../../Components/Studio/AnamnesisPromptModal";
import ClientAnamnesisOffcanvas from "../../../../Components/Studio/ClientAnamnesisOffcanvas";
import {
  Client,
  ClientDocumentType,
  createClient,
  CreateClientPayload,
  getClientById,
  getClients,
  updateClient,
} from "../../../../services/clientService";
import { PaginationMeta } from "../../../../types/api";
import ClientAddressFields, { ClientFormValues } from "./ClientAddressFields";
import ClientForm, { applyClientPhoneDuplicateError } from "./ClientForm";
import ClientsOptionsMenu from "./ClientsOptionsMenu";
import ClientMergeModal from "../../../../Components/Studio/ClientMergeModal";
import SmartImportWizard from "../../../../Components/Studio/SmartImportWizard";
import { formatCpfDisplay, isValidCpf, stripCpf } from "../../../../utils/cpf";
import {
  buildClientNotesTooltip,
  CLIENT_OPERATIONAL_NOTES_MAX_LENGTH,
  clientNotesNeedExpand,
  getClientNotesPinLine,
  hasClientOperationalNotes,
} from "../../../../helpers/clientNotes";
import {
  getClientListSignals,
  isClientProfileIncomplete,
  splitClientListSignals,
  type ClientListSignal,
} from "../../../../helpers/clientListSignals";
import {
  applyClientListFiltersToUrl,
  loadStoredClientListFilters,
  readClientListFiltersFromUrl,
  saveStoredClientListFilters,
  urlHasClientListFilters,
  type ClientListFilterSnapshot,
} from "../../../../helpers/clientListFiltersStorage";
import {
  buildServicePreferencesPayload,
  emptyServicePreferencesFormValues,
  getServicePreferencesPreview,
  hasClientServicePreferences,
  mapPreferencesToFormValues,
} from "../../../../helpers/clientServicePreferences";
import ClientNotesPreviewModal from "../../../../Components/Studio/ClientNotesPreviewModal";
import ClientServicePreferencesIndicator from "../../../../Components/Studio/ClientServicePreferencesIndicator";
import ClientTagChips from "../../../../Components/Studio/ClientTagChips";
import { getClientTags } from "../../../../services/clientTagService";
import { ClientTag } from "../../../../types/clientTag";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;

const defaultDocumentType = (locale: string): ClientDocumentType =>
  locale === "pt-BR" ? "cpf" : "national_id";

const emptyFormValues = (locale: string): ClientFormValues => ({
  name: "",
  phone_display: "",
  secondary_phone_display: "",
  email: null,
  birth_date: null,
  document_type: defaultDocumentType(locale),
  document_number_display: "",
  gender: "",
  instagram_handle: null,
  acquisition_source: null,
  allow_online_booking: true,
  source_registered_on: null,
  accepts_appointment_email: true,
  accepts_marketing_email: true,
  accepts_sms_marketing: true,
  street: null,
  number: null,
  neighborhood: null,
  complement: null,
  city: null,
  state: null,
  postal_code_display: "",
  country: "BR",
  notes: null,
  service_preferences: emptyServicePreferencesFormValues(),
});

const toNullable = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

const formatPhoneDisplay = (countryCode: string, phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, "");
  if (digits.length <= 2) {
    return digits.length ? `(${digits}` : "";
  }
  if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const formatPostalCodeDisplay = (postalCode?: string | null): string => {
  if (!postalCode) {
    return "";
  }

  const digits = postalCode.replace(/\D/g, "");
  if (digits.length <= 5) {
    return digits;
  }

  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
};

const formatPhoneLabel = (client: Client, fallback: string): string => {
  const display = formatPhoneDisplay(client.country_code, client.phone_number);
  return client.country_code === "55" ? display : `+${client.country_code} ${display}`;
};

const formatDocumentNumberDisplay = (
  documentType?: string | null,
  documentNumber?: string | null
): string => {
  if (!documentNumber) {
    return "";
  }

  if (documentType === "cpf") {
    return formatCpfDisplay(documentNumber);
  }

  return documentNumber;
};

const buildWhatsAppUrl = (client: Client): string | null => {
  const digits = client.phone_number?.replace(/\D/g, "") ?? "";

  if (!digits) {
    return null;
  }

  return `https://wa.me/${client.country_code}${digits}`;
};

const mapClientToFormValues = (client: Client, locale: string): ClientFormValues => ({
  name: client.name,
  phone_display: formatPhoneDisplay(client.country_code, client.phone_number),
  secondary_phone_display: client.secondary_phone_number
    ? formatPhoneDisplay(
        client.secondary_country_code ?? client.country_code,
        client.secondary_phone_number
      )
    : "",
  email: client.email ?? null,
  birth_date: client.birth_date ?? null,
  document_type: client.document_type ?? defaultDocumentType(locale),
  document_number_display: formatDocumentNumberDisplay(
    client.document_type,
    client.document_number
  ),
  gender: client.gender ?? "",
  instagram_handle: client.instagram_handle ?? null,
  acquisition_source: client.acquisition_source ?? null,
  allow_online_booking: client.allow_online_booking !== false,
  source_registered_on: client.source_registered_on ?? null,
  accepts_appointment_email: client.appointment_email_opt_out !== true,
  accepts_marketing_email: client.marketing_email_opt_out !== true,
  accepts_sms_marketing: client.sms_marketing_opt_out !== true,
  street: client.street ?? null,
  number: client.number ?? null,
  neighborhood: client.neighborhood ?? null,
  complement: client.complement ?? null,
  city: client.city ?? null,
  state: client.state ?? null,
  postal_code_display: formatPostalCodeDisplay(client.postal_code),
  country: client.country ?? "BR",
  notes: client.notes ?? null,
  service_preferences: mapPreferencesToFormValues(client.service_preferences),
});

const buildClientPayload = (values: ClientFormValues): CreateClientPayload => {
  const rawDocumentNumber = values.document_number_display.trim();
  const normalizedDocumentNumber =
    rawDocumentNumber === ""
      ? null
      : values.document_type === "cpf"
        ? stripCpf(rawDocumentNumber)
        : rawDocumentNumber.replace(/[^a-zA-Z0-9]/g, "");

  const secondaryDigits = values.secondary_phone_display.replace(/\D/g, "");
  const instagram = toNullable(values.instagram_handle)?.replace(/^@+/, "").toLowerCase() ?? null;

  return {
    name: values.name.trim(),
    country_code: "55",
    phone_number: values.phone_display.replace(/\D/g, ""),
    secondary_country_code: secondaryDigits ? "55" : null,
    secondary_phone_number: secondaryDigits || null,
    email: values.email,
    birth_date: toNullable(values.birth_date),
    document_type: normalizedDocumentNumber ? values.document_type : null,
    document_number: normalizedDocumentNumber,
    gender: values.gender === "" ? null : values.gender,
    instagram_handle: instagram,
    acquisition_source: toNullable(values.acquisition_source),
    allow_online_booking: values.allow_online_booking,
    source_registered_on: toNullable(values.source_registered_on),
    appointment_email_opt_out: !values.accepts_appointment_email,
    marketing_email_opt_out: !values.accepts_marketing_email,
    sms_marketing_opt_out: !values.accepts_sms_marketing,
    street: toNullable(values.street),
    number: toNullable(values.number),
    neighborhood: toNullable(values.neighborhood),
    complement: toNullable(values.complement),
    city: toNullable(values.city),
    state: toNullable(values.state),
    postal_code: toNullable(values.postal_code_display.replace(/\D/g, "")),
    country: toNullable(values.country?.toUpperCase() ?? null),
    notes: toNullable(values.notes),
    service_preferences: buildServicePreferencesPayload(values.service_preferences),
  };
};

const ClientsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  document.title = t("clients.pageTitle");

  const [clients, setClients] = useState<Client[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get("search")?.trim() ?? "");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editTargetId, setEditTargetId] = useState<number | null>(null);
  const [lastOptionsAction, setLastOptionsAction] =
    useState<ClientsOptionsActionPreference | null>(() =>
      getClientsLastOptionsActionForTenant(resolveCurrentTenantId())
    );
  const [anamnesisClient, setAnamnesisClient] = useState<Client | null>(null);
  const [anamnesisInitialTemplateId, setAnamnesisInitialTemplateId] = useState<number | null>(
    null
  );
  const deepLinkEditRef = useRef<number | null>(null);
  const deepLinkCreateRef = useRef<string | null>(null);
  const [createPrefill, setCreatePrefill] = useState<{
    phone_display?: string;
    name?: string;
  } | null>(null);
  const [anamnesisPromptClient, setAnamnesisPromptClient] = useState<Client | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeKeepId, setMergeKeepId] = useState<number | null>(null);
  const [notesPreview, setNotesPreview] = useState<{
    notes: string;
    clientName: string;
  } | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [activeCreditsOnly, setActiveCreditsOnly] = useState(
    () => searchParams.get("has_active_credits") === "1"
  );
  const [activeClubOnly, setActiveClubOnly] = useState(
    () => searchParams.get("has_active_club") === "1"
  );
  const [activeProtocolOnly, setActiveProtocolOnly] = useState(
    () => searchParams.get("has_active_protocol") === "1"
  );
  const [healthRecordsFilter, setHealthRecordsFilter] = useState<"all" | "with" | "without">(
    () => {
      const raw = searchParams.get("has_health_records");
      if (raw === "1") {
        return "with";
      }
      if (raw === "0") {
        return "without";
      }
      return "all";
    }
  );
  const [tagFilterId, setTagFilterId] = useState<number | null>(() => {
    const raw = searchParams.get("tag_id");
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });
  const [tagOptions, setTagOptions] = useState<ClientTag[]>([]);
  const [creditsExpiringWithinDays, setCreditsExpiringWithinDays] = useState<number | null>(
    () => {
      const raw = searchParams.get("credits_expiring_within_days");
      if (!raw) {
        return null;
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }
  );
  const [incompleteProfileOnly, setIncompleteProfileOnly] = useState(
    () => searchParams.get("incomplete_profile") === "1"
  );
  const [missingBirthDateOnly, setMissingBirthDateOnly] = useState(
    () =>
      searchParams.get("incomplete_profile") !== "1" &&
      searchParams.get("missing_birth_date") === "1"
  );
  const [missingInstagramOnly, setMissingInstagramOnly] = useState(
    () =>
      searchParams.get("incomplete_profile") !== "1" &&
      searchParams.get("missing_instagram") === "1"
  );
  const [missingAcquisitionSourceOnly, setMissingAcquisitionSourceOnly] = useState(
    () =>
      searchParams.get("incomplete_profile") !== "1" &&
      searchParams.get("missing_acquisition_source") === "1"
  );
  const [onlineBookingBlockedOnly, setOnlineBookingBlockedOnly] = useState(
    () => searchParams.get("online_booking_blocked") === "1"
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  const applyFilterSnapshot = useCallback(
    (snapshot: ClientListFilterSnapshot) => {
      setActiveCreditsOnly(snapshot.has_active_credits === "1");
      setActiveClubOnly(snapshot.has_active_club === "1");
      setActiveProtocolOnly(snapshot.has_active_protocol === "1");
      setCreditsExpiringWithinDays(() => {
        const raw = snapshot.credits_expiring_within_days;
        if (!raw) {
          return null;
        }
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      });
      setHealthRecordsFilter(
        snapshot.has_health_records === "1"
          ? "with"
          : snapshot.has_health_records === "0"
            ? "without"
            : "all"
      );
      setTagFilterId(() => {
        const raw = snapshot.tag_id;
        if (!raw) {
          return null;
        }
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      });
      const incomplete = snapshot.incomplete_profile === "1";
      setIncompleteProfileOnly(incomplete);
      setMissingBirthDateOnly(!incomplete && snapshot.missing_birth_date === "1");
      setMissingInstagramOnly(!incomplete && snapshot.missing_instagram === "1");
      setMissingAcquisitionSourceOnly(
        !incomplete && snapshot.missing_acquisition_source === "1"
      );
      setOnlineBookingBlockedOnly(snapshot.online_booking_blocked === "1");
    },
    []
  );

  useEffect(() => {
    if (filtersHydrated) {
      return;
    }

    if (urlHasClientListFilters(searchParams)) {
      saveStoredClientListFilters(readClientListFiltersFromUrl(searchParams));
      setFiltersHydrated(true);
      return;
    }

    const stored = loadStoredClientListFilters();
    if (stored && Object.keys(stored).length > 0) {
      applyFilterSnapshot(stored);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          applyClientListFiltersToUrl(next, stored);
          return next;
        },
        { replace: true }
      );
    }
    setFiltersHydrated(true);
  }, [applyFilterSnapshot, filtersHydrated, searchParams, setSearchParams]);

  useEffect(() => {
    if (!filtersHydrated) {
      return;
    }
    saveStoredClientListFilters(readClientListFiltersFromUrl(searchParams));
  }, [filtersHydrated, searchParams]);
  const [birthDateDrafts, setBirthDateDrafts] = useState<Record<number, string>>({});
  const [savingBirthDateId, setSavingBirthDateId] = useState<number | null>(null);
  const [bulkBirthDate, setBulkBirthDate] = useState("");
  const [savingBulkBirthDates, setSavingBulkBirthDates] = useState(false);

  const formInitialValues = useMemo(() => {
    if (editingClient) {
      return mapClientToFormValues(editingClient, i18n.language);
    }

    const base = emptyFormValues(i18n.language);
    if (!createPrefill) {
      return base;
    }

    return {
      ...base,
      ...(createPrefill.phone_display ? { phone_display: createPrefill.phone_display } : {}),
      ...(createPrefill.name ? { name: createPrefill.name } : {}),
    };
  }, [createPrefill, editingClient, i18n.language]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(t("clients.validation.nameRequired")),
        phone_display: Yup.string()
          .required(t("clients.validation.phoneRequired"))
          .test("phone-digits", t("clients.validation.phoneInvalid"), (value) => {
            const digits = (value || "").replace(/\D/g, "");
            return digits.length >= 10 && digits.length <= 11;
          }),
        secondary_phone_display: Yup.string()
          .trim()
          .test("secondary-phone-digits", t("clients.validation.phoneInvalid"), (value) => {
            const digits = (value || "").replace(/\D/g, "");
            return digits.length === 0 || (digits.length >= 10 && digits.length <= 11);
          }),
        email: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .email(t("clients.validation.emailInvalid")),
        birth_date: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .matches(/^\d{4}-\d{2}-\d{2}$/, t("clients.validation.birthDateInvalid")),
        gender: Yup.string()
          .oneOf(
            ["", "female", "male", "non_binary", "unspecified"],
            t("clients.validation.genderInvalid")
          )
          .nullable(),
        instagram_handle: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(64, t("clients.validation.instagramMax"))
          .test("instagram-format", t("clients.validation.instagramInvalid"), (value) => {
            if (!value) {
              return true;
            }
            const normalized = value.replace(/^@+/, "").toLowerCase();
            return /^[a-z0-9._]{1,64}$/.test(normalized);
          }),
        acquisition_source: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(120, t("clients.validation.acquisitionSourceMax")),
        source_registered_on: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .matches(/^\d{4}-\d{2}-\d{2}$/, t("clients.validation.sourceRegisteredOnInvalid")),
        document_type: Yup.string()
          .oneOf(
            ["cpf", "national_id", "passport", "other"],
            t("clients.validation.documentTypeInvalid")
          )
          .required(t("clients.validation.documentTypeInvalid")),
        document_number_display: Yup.string()
          .trim()
          .transform((value) => (value === "" ? "" : value))
          .max(50, t("clients.validation.documentNumberMax"))
          .when("document_type", {
            is: "cpf",
            then: (schema) =>
              schema.test("cpf-valid", t("clients.form.cpf_invalid"), (value) => {
                if (!value) {
                  return true;
                }

                return isValidCpf(value);
              }),
            otherwise: (schema) =>
              schema.test(
                "document-required",
                t("clients.validation.documentRequired"),
                (value) => !value || value.trim().length > 0
              ),
          }),
        street: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(255, t("clients.validation.streetMax")),
        number: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(20, t("clients.validation.numberMax")),
        neighborhood: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(100, t("clients.validation.neighborhoodMax")),
        complement: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(100, t("clients.validation.complementMax")),
        city: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(100, t("clients.validation.cityMax")),
        state: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(50, t("clients.validation.stateMax")),
        postal_code_display: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(20, t("clients.validation.postalCodeMax")),
        country: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value?.toUpperCase() ?? null))
          .nullable()
          .test("country-size", t("clients.validation.countrySize"), (value) => {
            if (!value) {
              return true;
            }
            return value.length === 2;
          }),
        notes: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .max(CLIENT_OPERATIONAL_NOTES_MAX_LENGTH, t("clients.validation.notesMax")),
      }),
    [t]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    activeCreditsOnly,
    activeClubOnly,
    activeProtocolOnly,
    creditsExpiringWithinDays,
    healthRecordsFilter,
    tagFilterId,
    incompleteProfileOnly,
    missingBirthDateOnly,
    missingInstagramOnly,
    missingAcquisitionSourceOnly,
    onlineBookingBlockedOnly,
  ]);

  useEffect(() => {
    let cancelled = false;
    getClientTags({ is_active: true, per_page: 100 })
      .then((response) => {
        if (!cancelled) {
          setTagOptions(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTagOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClients({
        page,
        per_page: PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(activeCreditsOnly && creditsExpiringWithinDays == null
          ? { has_active_credits: true }
          : {}),
        ...(activeClubOnly ? { has_active_club: true } : {}),
        ...(activeProtocolOnly ? { has_active_protocol: true } : {}),
        ...(creditsExpiringWithinDays != null
          ? { credits_expiring_within_days: creditsExpiringWithinDays }
          : {}),
        ...(healthRecordsFilter === "with"
          ? { has_health_records: true }
          : healthRecordsFilter === "without"
            ? { has_health_records: false }
            : {}),
        ...(tagFilterId != null ? { tag_id: tagFilterId } : {}),
        ...(incompleteProfileOnly
          ? { incomplete_profile: true }
          : {
              ...(missingBirthDateOnly ? { missing_birth_date: true } : {}),
              ...(missingInstagramOnly ? { missing_instagram: true } : {}),
              ...(missingAcquisitionSourceOnly
                ? { missing_acquisition_source: true }
                : {}),
            }),
        ...(onlineBookingBlockedOnly ? { online_booking_blocked: true } : {}),
      });
      setClients(response.data);
      setMeta(response.meta);
      if (missingBirthDateOnly && !incompleteProfileOnly) {
        const drafts: Record<number, string> = {};
        response.data.forEach((client) => {
          drafts[client.id] = "";
        });
        setBirthDateDrafts(drafts);
      }
    } catch (error: any) {
      toast.error(error?.data || t("clients.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    activeCreditsOnly,
    activeClubOnly,
    activeProtocolOnly,
    creditsExpiringWithinDays,
    healthRecordsFilter,
    tagFilterId,
    incompleteProfileOnly,
    missingBirthDateOnly,
    missingInstagramOnly,
    missingAcquisitionSourceOnly,
    onlineBookingBlockedOnly,
    t,
  ]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const refreshClientList = useCallback(async () => {
    if (page !== 1) {
      setPage(1);
      return;
    }

    await fetchClients();
  }, [page, fetchClients]);

  const formik = useFormik<ClientFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      const payload = buildClientPayload(values);

      try {
        if (editingClient) {
          const response = await updateClient(editingClient.id, payload);
          toast.success(response.message);
          closeOffcanvas();
          await refreshClientList();
        } else {
          const response = await createClient(payload);
          toast.success(response.message);
          closeOffcanvas();
          await refreshClientList();
          setAnamnesisPromptClient(response.data);
        }
      } catch (error: any) {
        if (applyClientPhoneDuplicateError(formik, error, t)) {
          return;
        }

        toast.error(error?.data || t("clients.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const closeOffcanvas = useCallback(() => {
    setOffcanvasOpen(false);
    setEditingClient(null);
    setEditTargetId(null);
    setFormLoading(false);
    setCreatePrefill(null);
  }, []);

  const openCreate = useCallback(
    (prefill?: { phone_display?: string; name?: string } | null) => {
      setEditingClient(null);
      setEditTargetId(null);
      setFormLoading(false);
      const phone = prefill?.phone_display?.trim() || "";
      const name = prefill?.name?.trim() || "";
      setCreatePrefill(
        phone || name
          ? {
              ...(phone ? { phone_display: phone } : {}),
              ...(name ? { name } : {}),
            }
          : null
      );
      setOffcanvasOpen(true);
    },
    []
  );

  const openEdit = useCallback(
    async (client: Client) => {
      setEditingClient(null);
      setEditTargetId(client.id);
      setFormLoading(true);
      setOffcanvasOpen(true);

      try {
        const response = await getClientById(client.id);
        setEditingClient(response.data);
      } catch (error: any) {
        toast.error(error?.data || t("clients.toast.loadError"));
        closeOffcanvas();
      } finally {
        setFormLoading(false);
      }
    },
    [closeOffcanvas, t]
  );

  useEffect(() => {
    const editParam = searchParams.get("edit");

    if (!editParam) {
      deepLinkEditRef.current = null;
      return;
    }

    const parsedClientId = Number(editParam);

    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    if (deepLinkEditRef.current === parsedClientId) {
      return;
    }

    deepLinkEditRef.current = parsedClientId;
    setSearchParams({}, { replace: true });
    void openEdit({ id: parsedClientId } as Client);
  }, [openEdit, searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("import") !== "1") {
      return;
    }
    setImportModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("import");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("create") !== "1") {
      deepLinkCreateRef.current = null;
      return;
    }

    const rawPhone = (searchParams.get("phone") || "").replace(/\D/g, "");
    const rawName = (searchParams.get("name") || "").trim().slice(0, 80);
    const signature = `create:${rawPhone}:${rawName}`;

    if (deepLinkCreateRef.current === signature) {
      return;
    }

    deepLinkCreateRef.current = signature;

    let localDigits = rawPhone;
    if (localDigits.startsWith("55") && localDigits.length >= 12) {
      localDigits = localDigits.slice(2);
    }

    const phoneDisplay = localDigits ? formatPhoneDisplay("55", localDigits) : "";
    openCreate({
      ...(phoneDisplay ? { phone_display: phoneDisplay } : {}),
      ...(rawName ? { name: rawName } : {}),
    });

    const next = new URLSearchParams(searchParams);
    next.delete("create");
    next.delete("phone");
    next.delete("name");
    next.delete("country_code");
    setSearchParams(next, { replace: true });
  }, [openCreate, searchParams, setSearchParams]);

  const closeAnamnesisOffcanvas = () => {
    setAnamnesisClient(null);
    setAnamnesisInitialTemplateId(null);
    void fetchClients();
  };

  const handleAnamnesisLater = () => {
    setAnamnesisPromptClient(null);
  };

  const handleAnamnesisConfirm = (templateId: number | null) => {
    if (!anamnesisPromptClient) {
      return;
    }

    setAnamnesisInitialTemplateId(templateId);
    setAnamnesisClient(anamnesisPromptClient);
    setAnamnesisPromptClient(null);
  };

  const handleRememberOptionsAction = useCallback(
    (action: ClientsOptionsActionPreference) => {
      setLastOptionsAction(action);
      setClientsLastOptionsActionForTenant(resolveCurrentTenantId(), action);
    },
    []
  );

  const clientsGuideUrl = useMemo(
    () => getHelpArticleUrl(HELP_ARTICLE_CLIENTS, i18n.language),
    [i18n.language]
  );

  const chromeListSummary = useMemo(() => {
    const total = meta?.total ?? clients.length;
    const incomplete = meta?.incomplete_profile_count ?? 0;

    return t("clients.chrome.summary", {
      count: total,
      incomplete,
    });
  }, [clients.length, meta?.incomplete_profile_count, meta?.total, t]);

  const saveBirthDateInline = async (client: Client) => {
    const draft = (birthDateDrafts[client.id] ?? "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft)) {
      toast.error(t("clients.validation.birthDateInvalid"));
      return;
    }

    setSavingBirthDateId(client.id);
    try {
      await updateClient(client.id, { birth_date: draft });
      toast.success(t("clients.toast.birthDateSaved"));
      await fetchClients();
    } catch (error: any) {
      toast.error(error?.data || t("clients.toast.saveError"));
    } finally {
      setSavingBirthDateId(null);
    }
  };

  const applyBulkBirthDateToDrafts = () => {
    const date = bulkBirthDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast.error(t("clients.validation.birthDateInvalid"));
      return;
    }

    const next: Record<number, string> = {};
    clients.forEach((client) => {
      next[client.id] = date;
    });
    setBirthDateDrafts(next);
  };

  const saveBulkBirthDates = async () => {
    const date = bulkBirthDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast.error(t("clients.validation.birthDateInvalid"));
      return;
    }

    if (clients.length === 0) {
      return;
    }

    setSavingBulkBirthDates(true);
    let saved = 0;
    try {
      for (const client of clients) {
        await updateClient(client.id, { birth_date: date });
        saved += 1;
      }
      toast.success(t("clients.toast.birthDateBulkSaved", { count: saved }));
      setBulkBirthDate("");
      await fetchClients();
    } catch (error: any) {
      toast.error(
        saved > 0
          ? t("clients.toast.birthDateBulkPartial", { saved })
          : error?.data || t("clients.toast.saveError")
      );
      await fetchClients();
    } finally {
      setSavingBulkBirthDates(false);
    }
  };

  const totalPages = meta?.last_page ?? 1;

  const clearListFilters = useCallback(() => {
    setActiveCreditsOnly(false);
    setActiveClubOnly(false);
    setActiveProtocolOnly(false);
    setCreditsExpiringWithinDays(null);
    setHealthRecordsFilter("all");
    setTagFilterId(null);
    setIncompleteProfileOnly(false);
    setMissingBirthDateOnly(false);
    setMissingInstagramOnly(false);
    setMissingAcquisitionSourceOnly(false);
    setOnlineBookingBlockedOnly(false);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        [
          "has_active_credits",
          "has_active_club",
          "has_active_protocol",
          "credits_expiring_within_days",
          "has_health_records",
          "tag_id",
          "incomplete_profile",
          "missing_birth_date",
          "missing_instagram",
          "missing_acquisition_source",
          "online_booking_blocked",
          "hint",
        ].forEach((key) => next.delete(key));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const patchFilterParam = useCallback(
    (mutator: (next: URLSearchParams) => void) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutator(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onClear: () => void }> = [];

    if (incompleteProfileOnly) {
      chips.push({
        key: "incomplete_profile",
        label: t("clients.filter.incompleteProfile"),
        onClear: () => {
          setIncompleteProfileOnly(false);
          patchFilterParam((next) => next.delete("incomplete_profile"));
        },
      });
    }
    if (missingBirthDateOnly) {
      chips.push({
        key: "missing_birth_date",
        label: t("clients.filter.missingBirthDate"),
        onClear: () => {
          setMissingBirthDateOnly(false);
          patchFilterParam((next) => {
            next.delete("missing_birth_date");
            next.delete("hint");
          });
        },
      });
    }
    if (missingInstagramOnly) {
      chips.push({
        key: "missing_instagram",
        label: t("clients.filter.missingInstagram"),
        onClear: () => {
          setMissingInstagramOnly(false);
          patchFilterParam((next) => next.delete("missing_instagram"));
        },
      });
    }
    if (missingAcquisitionSourceOnly) {
      chips.push({
        key: "missing_acquisition_source",
        label: t("clients.filter.missingAcquisitionSource"),
        onClear: () => {
          setMissingAcquisitionSourceOnly(false);
          patchFilterParam((next) => next.delete("missing_acquisition_source"));
        },
      });
    }
    if (onlineBookingBlockedOnly) {
      chips.push({
        key: "online_booking_blocked",
        label: t("clients.filter.onlineBookingBlocked"),
        onClear: () => {
          setOnlineBookingBlockedOnly(false);
          patchFilterParam((next) => next.delete("online_booking_blocked"));
        },
      });
    }
    if (activeCreditsOnly && creditsExpiringWithinDays == null) {
      chips.push({
        key: "has_active_credits",
        label: t("clients.filter.activeCreditsOnly"),
        onClear: () => {
          setActiveCreditsOnly(false);
          patchFilterParam((next) => next.delete("has_active_credits"));
        },
      });
    }
    if (creditsExpiringWithinDays != null) {
      chips.push({
        key: "credits_expiring",
        label: t("clients.filter.creditsExpiringSoon"),
        onClear: () => {
          setCreditsExpiringWithinDays(null);
          patchFilterParam((next) => next.delete("credits_expiring_within_days"));
        },
      });
    }
    if (activeClubOnly) {
      chips.push({
        key: "has_active_club",
        label: t("clients.filter.activeClubOnly"),
        onClear: () => {
          setActiveClubOnly(false);
          patchFilterParam((next) => next.delete("has_active_club"));
        },
      });
    }
    if (activeProtocolOnly) {
      chips.push({
        key: "has_active_protocol",
        label: t("clients.filter.activeProtocolOnly"),
        onClear: () => {
          setActiveProtocolOnly(false);
          patchFilterParam((next) => next.delete("has_active_protocol"));
        },
      });
    }
    if (healthRecordsFilter === "with") {
      chips.push({
        key: "health_with",
        label: t("clients.filter.healthRecordsWith"),
        onClear: () => {
          setHealthRecordsFilter("all");
          patchFilterParam((next) => next.delete("has_health_records"));
        },
      });
    }
    if (healthRecordsFilter === "without") {
      chips.push({
        key: "health_without",
        label: t("clients.filter.healthRecordsWithout"),
        onClear: () => {
          setHealthRecordsFilter("all");
          patchFilterParam((next) => next.delete("has_health_records"));
        },
      });
    }
    if (tagFilterId != null) {
      const tagName =
        tagOptions.find((tag) => tag.id === tagFilterId)?.name ??
        t("clients.filter.tags");
      chips.push({
        key: "tag",
        label: tagName,
        onClear: () => {
          setTagFilterId(null);
          patchFilterParam((next) => next.delete("tag_id"));
        },
      });
    }

    return chips;
  }, [
    activeClubOnly,
    activeCreditsOnly,
    activeProtocolOnly,
    creditsExpiringWithinDays,
    healthRecordsFilter,
    incompleteProfileOnly,
    missingAcquisitionSourceOnly,
    missingBirthDateOnly,
    missingInstagramOnly,
    onlineBookingBlockedOnly,
    patchFilterParam,
    t,
    tagFilterId,
    tagOptions,
  ]);

  const activeFilterCount = activeFilterChips.length;
  const hasListFilters = activeFilterCount > 0;
  const hasActiveSearch = debouncedSearch.length > 0 || hasListFilters;
  const notAvailable = t("clients.common.notAvailable");

  const signalBadgeLabel = (signal: ClientListSignal, client: Client): string => {
    switch (signal) {
      case "onlineBookingBlocked":
        return t("clients.badges.onlineBookingBlocked");
      case "incompleteProfile":
        return t("clients.badges.incompleteProfile");
      case "activeSessions":
        return t("clients.badges.activeSessions", {
          count: client.active_credits_count,
        });
      case "club":
        return t("clients.badges.club");
      case "activeProtocol":
        return t("clients.badges.activeProtocol", {
          count: client.active_protocols_count,
        });
      case "loyalty":
        return t("clients.badges.loyalty");
      case "healthRecords":
        return t("clients.badges.healthRecords", {
          count: client.health_records_count,
        });
      default:
        return "";
    }
  };

  const signalBadgeColor = (signal: ClientListSignal): string => {
    switch (signal) {
      case "onlineBookingBlocked":
        return "soft-secondary";
      case "incompleteProfile":
        return "soft-warning";
      case "activeSessions":
        return "soft-info";
      case "club":
        return "soft-primary";
      case "activeProtocol":
        return "soft-success";
      case "loyalty":
        return "soft-success";
      case "healthRecords":
        return "soft-primary";
      default:
        return "soft-secondary";
    }
  };

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("clients.breadcrumb.title")}
            pageTitle={t("clients.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="clients-card-header--chrome">
                  <div className="clients-chrome-row clients-chrome-row--primary">
                    <div className="clients-chrome-title">
                      <h4 className="card-title mb-0">{t("clients.card.title")}</h4>
                      <p className="clients-chrome-summary text-muted small mb-0 mt-1">
                        {chromeListSummary}
                      </p>
                    </div>
                    <div className="clients-chrome-toolbar">
                      <Button color="success" size="sm" onClick={() => openCreate()}>
                        <i className="ri-add-line align-bottom me-1" />
                        {t("clients.actions.new")}
                      </Button>
                      <ClientsOptionsMenu
                        guideUrl={clientsGuideUrl}
                        lastAction={lastOptionsAction}
                        onRememberAction={handleRememberOptionsAction}
                        onImport={() => setImportModalOpen(true)}
                        onMerge={() => {
                          setMergeKeepId(null);
                          setMergeModalOpen(true);
                        }}
                      />
                    </div>
                  </div>
                  <div className="clients-chrome-row">
                    <div className="clients-chrome-search search-box">
                      <Input
                        type="text"
                        className="form-control"
                        placeholder={t("clients.search.placeholder")}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                      <i className="ri-search-line search-icon" />
                    </div>
                    <div className="clients-chrome-filters">
                      {activeFilterChips.length > 0 ? (
                        <div className="d-flex flex-wrap align-items-center gap-1">
                          {activeFilterChips.map((chip) => (
                            <Badge
                              key={chip.key}
                              color="light"
                              className="text-body border border-secondary-subtle fw-normal"
                            >
                              {chip.label}
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ms-1 text-body"
                                onClick={chip.onClear}
                                title={t("clients.filter.removeChip")}
                                aria-label={t("clients.filter.removeChip")}
                              >
                                <i className="ri-close-line" aria-hidden="true" />
                              </button>
                            </Badge>
                          ))}
                          <Button
                            color="link"
                            size="sm"
                            className="px-1 py-0"
                            type="button"
                            onClick={clearListFilters}
                          >
                            {t("clients.filter.clearAll")}
                          </Button>
                        </div>
                      ) : null}
                      <Button
                        color={hasListFilters ? "success" : "secondary"}
                        outline
                        size="sm"
                        className={
                          hasListFilters
                            ? "clients-filters-toggle--active"
                            : "bg-white text-body border-secondary-subtle"
                        }
                        onClick={() => setFiltersOpen(true)}
                      >
                        <i className="ri-filter-3-line align-middle me-1" aria-hidden="true" />
                        {t("clients.filter.open")}
                        {activeFilterCount > 0 ? (
                          <Badge color="success" pill className="ms-1 align-middle">
                            {activeFilterCount}
                          </Badge>
                        ) : null}
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardBody>
                  {(searchParams.get("hint") === "birthday" || missingBirthDateOnly) && (
                    <Alert color="info" className="border-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <span>
                        {missingBirthDateOnly
                          ? t("clients.hint.missingBirthDate")
                          : t("clients.hint.birthday")}
                      </span>
                      <div className="d-flex flex-wrap gap-2">
                        {!missingBirthDateOnly && (
                          <Button color="primary" size="sm" onClick={() => openCreate()}>
                            {t("clients.hint.birthdayCreate")}
                          </Button>
                        )}
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => {
                            setMissingBirthDateOnly(false);
                            setSearchParams(
                              (current) => {
                                const next = new URLSearchParams(current);
                                next.delete("hint");
                                next.delete("missing_birth_date");
                                return next;
                              },
                              { replace: true }
                            );
                          }}
                        >
                          {t("clients.hint.birthdayDismiss")}
                        </Button>
                      </div>
                    </Alert>
                  )}
                  <Offcanvas
                    direction="end"
                    isOpen={filtersOpen}
                    toggle={() => setFiltersOpen(false)}
                    style={{ width: 360 }}
                  >
                    <OffcanvasHeader toggle={() => setFiltersOpen(false)}>
                      {t("clients.filter.title")}
                    </OffcanvasHeader>
                    <OffcanvasBody>
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <div className="text-muted text-uppercase fs-11 fw-semibold mb-2">
                            {t("clients.filter.section.profile")}
                          </div>
                          <div className="d-flex flex-column gap-2">
                            <div className="form-check form-switch">
                              <Input
                                id="clients-incomplete-profile-filter"
                                type="switch"
                                role="switch"
                                checked={incompleteProfileOnly}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setIncompleteProfileOnly(checked);
                                  if (checked) {
                                    setMissingBirthDateOnly(false);
                                    setMissingInstagramOnly(false);
                                    setMissingAcquisitionSourceOnly(false);
                                  }
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("incomplete_profile", "1");
                                      next.delete("missing_birth_date");
                                      next.delete("missing_instagram");
                                      next.delete("missing_acquisition_source");
                                      next.delete("hint");
                                    } else {
                                      next.delete("incomplete_profile");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-incomplete-profile-filter"
                              >
                                {t("clients.filter.incompleteProfile")}
                              </Label>
                              <FormText muted className="d-block small">
                                {t("clients.filter.incompleteProfileHint")}
                              </FormText>
                            </div>
                            <div className="form-check form-switch">
                              <Input
                                id="clients-missing-birth-date-filter"
                                type="switch"
                                role="switch"
                                checked={missingBirthDateOnly}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setMissingBirthDateOnly(checked);
                                  if (checked) {
                                    setIncompleteProfileOnly(false);
                                  }
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("missing_birth_date", "1");
                                      next.delete("incomplete_profile");
                                    } else {
                                      next.delete("missing_birth_date");
                                      next.delete("hint");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-missing-birth-date-filter"
                              >
                                {t("clients.filter.missingBirthDate")}
                              </Label>
                              <FormText muted className="d-block small">
                                {t("clients.filter.missingBirthDateHint")}
                              </FormText>
                            </div>
                            <div className="form-check form-switch">
                              <Input
                                id="clients-missing-instagram-filter"
                                type="switch"
                                role="switch"
                                checked={missingInstagramOnly}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setMissingInstagramOnly(checked);
                                  if (checked) {
                                    setIncompleteProfileOnly(false);
                                  }
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("missing_instagram", "1");
                                      next.delete("incomplete_profile");
                                    } else {
                                      next.delete("missing_instagram");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-missing-instagram-filter"
                              >
                                {t("clients.filter.missingInstagram")}
                              </Label>
                            </div>
                            <div className="form-check form-switch">
                              <Input
                                id="clients-missing-acquisition-filter"
                                type="switch"
                                role="switch"
                                checked={missingAcquisitionSourceOnly}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setMissingAcquisitionSourceOnly(checked);
                                  if (checked) {
                                    setIncompleteProfileOnly(false);
                                  }
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("missing_acquisition_source", "1");
                                      next.delete("incomplete_profile");
                                    } else {
                                      next.delete("missing_acquisition_source");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-missing-acquisition-filter"
                              >
                                {t("clients.filter.missingAcquisitionSource")}
                              </Label>
                            </div>
                            <div className="form-check form-switch">
                              <Input
                                id="clients-online-booking-blocked-filter"
                                type="switch"
                                role="switch"
                                checked={onlineBookingBlockedOnly}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setOnlineBookingBlockedOnly(checked);
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("online_booking_blocked", "1");
                                    } else {
                                      next.delete("online_booking_blocked");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-online-booking-blocked-filter"
                              >
                                {t("clients.filter.onlineBookingBlocked")}
                              </Label>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="text-muted text-uppercase fs-11 fw-semibold mb-2">
                            {t("clients.filter.section.operations")}
                          </div>
                          <div className="d-flex flex-column gap-2">
                            <div className="form-check form-switch">
                              <Input
                                id="clients-active-credits-filter"
                                type="switch"
                                role="switch"
                                checked={activeCreditsOnly && creditsExpiringWithinDays == null}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setActiveCreditsOnly(checked);
                                  setCreditsExpiringWithinDays(null);
                                  patchFilterParam((next) => {
                                    next.delete("credits_expiring_within_days");
                                    if (checked) {
                                      next.set("has_active_credits", "1");
                                    } else {
                                      next.delete("has_active_credits");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-active-credits-filter"
                              >
                                {t("clients.filter.activeCreditsOnly")}
                              </Label>
                            </div>
                            <div className="form-check form-switch">
                              <Input
                                id="clients-credits-expiring-filter"
                                type="switch"
                                role="switch"
                                checked={creditsExpiringWithinDays != null}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setCreditsExpiringWithinDays(checked ? 30 : null);
                                  if (checked) {
                                    setActiveCreditsOnly(false);
                                  }
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("credits_expiring_within_days", "30");
                                      next.delete("has_active_credits");
                                    } else {
                                      next.delete("credits_expiring_within_days");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-credits-expiring-filter"
                              >
                                {t("clients.filter.creditsExpiringSoon")}
                              </Label>
                            </div>
                            <div className="form-check form-switch">
                              <Input
                                id="clients-active-club-filter"
                                type="switch"
                                role="switch"
                                checked={activeClubOnly}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setActiveClubOnly(checked);
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("has_active_club", "1");
                                    } else {
                                      next.delete("has_active_club");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-active-club-filter"
                              >
                                {t("clients.filter.activeClubOnly")}
                              </Label>
                            </div>
                            <div className="form-check form-switch">
                              <Input
                                id="clients-active-protocol-filter"
                                type="switch"
                                role="switch"
                                checked={activeProtocolOnly}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setActiveProtocolOnly(checked);
                                  patchFilterParam((next) => {
                                    if (checked) {
                                      next.set("has_active_protocol", "1");
                                    } else {
                                      next.delete("has_active_protocol");
                                    }
                                  });
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="clients-active-protocol-filter"
                              >
                                {t("clients.filter.activeProtocolOnly")}
                              </Label>
                            </div>
                          </div>
                        </div>

                        <div>
                          <Label
                            className="form-label mb-1"
                            htmlFor="clients-health-records-filter"
                          >
                            {t("clients.filter.healthRecords")}
                          </Label>
                          <Input
                            id="clients-health-records-filter"
                            type="select"
                            value={healthRecordsFilter}
                            onChange={(event) => {
                              const nextValue = event.target.value as
                                | "all"
                                | "with"
                                | "without";
                              setHealthRecordsFilter(nextValue);
                              patchFilterParam((next) => {
                                if (nextValue === "with") {
                                  next.set("has_health_records", "1");
                                } else if (nextValue === "without") {
                                  next.set("has_health_records", "0");
                                } else {
                                  next.delete("has_health_records");
                                }
                              });
                            }}
                          >
                            <option value="all">{t("clients.filter.healthRecordsAll")}</option>
                            <option value="with">{t("clients.filter.healthRecordsWith")}</option>
                            <option value="without">
                              {t("clients.filter.healthRecordsWithout")}
                            </option>
                          </Input>
                        </div>

                        <div>
                          <Label className="form-label mb-1" htmlFor="clients-tag-filter">
                            {t("clients.filter.tags")}
                          </Label>
                          <Input
                            id="clients-tag-filter"
                            type="select"
                            value={tagFilterId ?? ""}
                            onChange={(event) => {
                              const raw = event.target.value;
                              const nextId = raw === "" ? null : Number(raw);
                              setTagFilterId(
                                nextId != null && Number.isFinite(nextId) && nextId > 0
                                  ? nextId
                                  : null
                              );
                              patchFilterParam((next) => {
                                if (nextId != null && Number.isFinite(nextId) && nextId > 0) {
                                  next.set("tag_id", String(nextId));
                                } else {
                                  next.delete("tag_id");
                                }
                              });
                            }}
                          >
                            <option value="">{t("clients.filter.tagsAll")}</option>
                            {tagOptions.length === 0 ? (
                              <option value="" disabled>
                                {t("clients.filter.tagsEmpty")}
                              </option>
                            ) : (
                              tagOptions.map((tag) => (
                                <option key={tag.id} value={tag.id}>
                                  {tag.name}
                                </option>
                              ))
                            )}
                          </Input>
                        </div>

                        <div className="d-flex gap-2 mt-2">
                          <Button
                            color="light"
                            className="flex-grow-1"
                            onClick={clearListFilters}
                            disabled={!hasListFilters}
                          >
                            {t("clients.filter.clearAll")}
                          </Button>
                          <Button
                            color="primary"
                            className="flex-grow-1"
                            onClick={() => setFiltersOpen(false)}
                          >
                            {t("clients.filter.apply")}
                          </Button>
                        </div>
                      </div>
                    </OffcanvasBody>
                  </Offcanvas>
                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : clients.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">
                        {missingBirthDateOnly && !incompleteProfileOnly
                          ? t("clients.empty.missingBirthDate")
                          : incompleteProfileOnly
                            ? t("clients.empty.incompleteProfile")
                            : hasListFilters
                              ? t("clients.empty.filtered")
                              : hasActiveSearch
                                ? t("clients.empty.noResults", { term: debouncedSearch })
                                : t("clients.empty.noData")}
                      </p>
                      {hasListFilters ? (
                        <Button color="soft-secondary" onClick={clearListFilters}>
                          {t("clients.filter.clearAll")}
                        </Button>
                      ) : !hasActiveSearch ? (
                        <Button color="soft-primary" onClick={() => openCreate()}>
                          {t("clients.actions.createFirst")}
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      {missingBirthDateOnly && !incompleteProfileOnly ? (
                        <Alert
                          color="light"
                          className="border d-flex flex-wrap align-items-end gap-2 mb-3"
                        >
                          <div className="flex-grow-1" style={{ minWidth: 180 }}>
                            <Label
                              className="form-label mb-1"
                              htmlFor="clients-bulk-birth-date"
                            >
                              {t("clients.bulkBirthDate.label")}
                            </Label>
                            <Input
                              id="clients-bulk-birth-date"
                              type="date"
                              value={bulkBirthDate}
                              onChange={(event) => setBulkBirthDate(event.target.value)}
                            />
                          </div>
                          <Button
                            color="light"
                            size="sm"
                            disabled={!bulkBirthDate || savingBulkBirthDates}
                            onClick={applyBulkBirthDateToDrafts}
                          >
                            {t("clients.bulkBirthDate.fillPage")}
                          </Button>
                          <Button
                            color="soft-success"
                            size="sm"
                            disabled={!bulkBirthDate || savingBulkBirthDates}
                            onClick={() => void saveBulkBirthDates()}
                          >
                            {savingBulkBirthDates ? (
                              <Spinner size="sm" />
                            ) : (
                              t("clients.bulkBirthDate.savePage", {
                                count: clients.length,
                              })
                            )}
                          </Button>
                          <span className="text-muted small">
                            {t("clients.bulkBirthDate.hint")}
                          </span>
                        </Alert>
                      ) : null}
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0 clients-table">
                          <thead className="table-light">
                            <tr>
                              <th>{t("clients.table.name")}</th>
                              <th>{t("clients.table.status")}</th>
                              <th>{t("clients.table.phone")}</th>
                              <th>{t("clients.table.email")}</th>
                              <th>{t("clients.table.lastService")}</th>
                              {missingBirthDateOnly && !incompleteProfileOnly && (
                                <th style={{ minWidth: 220 }}>
                                  {t("clients.table.birthDate")}
                                </th>
                              )}
                              <th className="text-end" style={{ width: 200 }}>
                                {t("clients.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {clients.map((client) => {
                              const whatsAppUrl = buildWhatsAppUrl(client);
                              const documentLabel = formatDocumentNumberDisplay(
                                client.document_type,
                                client.document_number
                              );
                              const signals = getClientListSignals(client).filter(
                                (signal) => signal !== "incompleteProfile"
                              );
                              const { visible: visibleSignals, overflow: signalOverflow } =
                                splitClientListSignals(signals);
                              const isIncomplete = isClientProfileIncomplete(client);

                              return (
                              <tr
                                key={client.id}
                                onClick={() => navigate(`/clients/${client.id}`)}
                              >
                                <td className="clients-table__name">
                                  <div>{client.name}</div>
                                  {documentLabel ? (
                                    <span className="clients-table__document">
                                      {documentLabel}
                                    </span>
                                  ) : null}
                                  <div className="clients-table__secondary d-flex flex-wrap align-items-center gap-1 mt-1">
                                    {visibleSignals.map((signal) => (
                                      <Badge
                                        key={signal}
                                        color={signalBadgeColor(signal)}
                                        className="fw-normal"
                                      >
                                        {signalBadgeLabel(signal, client)}
                                      </Badge>
                                    ))}
                                    {signalOverflow > 0 ? (
                                      <Badge color="soft-secondary" className="fw-normal">
                                        {t("clients.badges.more", { count: signalOverflow })}
                                      </Badge>
                                    ) : null}
                                    {hasClientOperationalNotes(client.notes) ? (
                                      <button
                                        type="button"
                                        className="badge bg-warning-subtle text-warning fw-normal border-0"
                                        title={buildClientNotesTooltip(
                                          client.notes!.trim(),
                                          (preview) =>
                                            t("clients.picker.notesTooltip", { preview })
                                        )}
                                        aria-label={t("clients.picker.viewNotesAria")}
                                        data-clarity-mask="true"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          setNotesPreview({
                                            notes: client.notes!.trim(),
                                            clientName: client.name,
                                          });
                                        }}
                                      >
                                        <i
                                          className="ri-sticky-note-line me-1"
                                          aria-hidden="true"
                                        />
                                        {getClientNotesPinLine(client.notes, 40)}
                                        {clientNotesNeedExpand(client.notes, 40) ? (
                                          <span className="ms-1 text-decoration-underline">
                                            {t("clients.notesPreview.viewAll")}
                                          </span>
                                        ) : null}
                                      </button>
                                    ) : null}
                                    {hasClientServicePreferences(client.service_preferences) ? (
                                      <ClientServicePreferencesIndicator
                                        tooltip={t("clients.picker.preferencesTooltip", {
                                          preview:
                                            getServicePreferencesPreview(
                                              client.service_preferences
                                            ) ?? t("clients.preferences.chipFallback"),
                                        })}
                                        ariaLabel={t("clients.picker.viewPreferencesAria")}
                                        preview={getServicePreferencesPreview(
                                          client.service_preferences,
                                          2,
                                          40
                                        )}
                                      />
                                    ) : null}
                                    <ClientTagChips tags={client.tags} maxVisible={2} />
                                  </div>
                                </td>
                                <td>
                                  {isIncomplete ? (
                                    <button
                                      type="button"
                                      className="badge bg-warning-subtle text-warning fw-normal border-0"
                                      title={t("clients.badges.incompleteProfileHint")}
                                      aria-label={t("clients.badges.incompleteProfileHint")}
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        void openEdit(client);
                                      }}
                                    >
                                      {t("clients.badges.incomplete")}
                                    </button>
                                  ) : (
                                    <span className="text-muted">{notAvailable}</span>
                                  )}
                                </td>
                                <td>{formatPhoneLabel(client, notAvailable)}</td>
                                <td className="text-muted">{client.email ?? notAvailable}</td>
                                <td>
                                  {client.last_service_name?.trim()
                                    ? client.last_service_name
                                    : notAvailable}
                                </td>
                                {missingBirthDateOnly && !incompleteProfileOnly && (
                                  <td onClick={(event) => event.stopPropagation()}>
                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                      <Input
                                        type="date"
                                        bsSize="sm"
                                        value={birthDateDrafts[client.id] ?? ""}
                                        onChange={(event) =>
                                          setBirthDateDrafts((current) => ({
                                            ...current,
                                            [client.id]: event.target.value,
                                          }))
                                        }
                                        aria-label={t("clients.table.birthDate")}
                                      />
                                      <Button
                                        color="soft-success"
                                        size="sm"
                                        disabled={savingBirthDateId === client.id}
                                        onClick={() => void saveBirthDateInline(client)}
                                      >
                                        {savingBirthDateId === client.id ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          t("clients.actions.saveBirthDate")
                                        )}
                                      </Button>
                                    </div>
                                  </td>
                                )}
                                <td
                                  className="text-end clients-table__actions"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  {whatsAppUrl ? (
                                    <a
                                      href={whatsAppUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-soft-success btn-sm me-1"
                                      title={t("studio.clients.actions.whatsapp")}
                                      aria-label={t("studio.clients.actions.whatsapp")}
                                    >
                                      <i className="ri-whatsapp-line" />
                                    </a>
                                  ) : null}
                                  <Button
                                    tag={Link}
                                    to={`/agenda?client_id=${client.id}`}
                                    color="soft-primary"
                                    size="sm"
                                    className="me-1"
                                    title={t("studio.clients.actions.book")}
                                  >
                                    <i className="ri-calendar-line" />
                                  </Button>
                                  <Button
                                    tag={Link}
                                    to={`/orders?client_id=${client.id}&action=new`}
                                    color="soft-warning"
                                    size="sm"
                                    className="me-1"
                                    title={t("studio.clients.actions.order")}
                                  >
                                    <i className="ri-file-list-3-line" />
                                  </Button>
                                  <Button
                                    color="soft-success"
                                    size="sm"
                                    className="me-1"
                                    title={t("clients.actions.anamnesis")}
                                    onClick={() => {
                                      setAnamnesisInitialTemplateId(null);
                                      setAnamnesisClient(client);
                                    }}
                                  >
                                    <i className="ri-health-book-line" />
                                  </Button>
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    title={t("clients.actions.edit")}
                                    onClick={() => openEdit(client)}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("clients.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("clients.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("clients.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ClientAnamnesisOffcanvas
        isOpen={anamnesisClient !== null}
        onClose={closeAnamnesisOffcanvas}
        clientId={anamnesisClient?.id ?? 0}
        clientName={anamnesisClient?.name ?? ""}
        initialTemplateId={anamnesisInitialTemplateId}
      />

      <AnamnesisPromptModal
        isOpen={anamnesisPromptClient !== null}
        clientName={anamnesisPromptClient?.name ?? ""}
        onLater={handleAnamnesisLater}
        onConfirm={handleAnamnesisConfirm}
      />

      <SmartImportWizard
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        entityType="clients"
        onCompleted={() => {
          void fetchClients();
        }}
        onReviewDuplicates={() => {
          setMergeKeepId(null);
          setMergeModalOpen(true);
        }}
      />

      <ClientMergeModal
        isOpen={mergeModalOpen}
        initialKeepId={mergeKeepId}
        onClose={() => {
          setMergeModalOpen(false);
          setMergeKeepId(null);
        }}
        onMerged={() => {
          void fetchClients();
        }}
      />

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        className="border-0"
      >
        <OffcanvasHeader toggle={closeOffcanvas} className="bg-light">
          {editTargetId ? t("clients.form.editTitle") : t("clients.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
            <Form
              key={
                editingClient
                  ? `edit-${editingClient.id}`
                  : createPrefill
                    ? `create-${createPrefill.phone_display || ""}-${createPrefill.name || ""}`
                    : "create"
              }
              onSubmit={(event) => {
                event.preventDefault();
                formik.handleSubmit();
              }}
            >
              <h6 className="text-muted text-uppercase fs-12 mb-3">
                {t("clients.form.section.contact")}
              </h6>

              <div className="mb-3">
                <Label htmlFor="client-name">{t("clients.fields.name.label")}</Label>
                <Input
                  id="client-name"
                  name="name"
                  placeholder={t("clients.fields.name.placeholder")}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.name && formik.errors.name)}
                />
                {formik.touched.name && formik.errors.name ? (
                  <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                ) : null}
              </div>

              <ClientForm formik={formik} editingClientId={editingClient?.id ?? null} />

              <ClientAddressFields formik={formik} />

              <div className="d-flex gap-2 mt-4">
                <Button color="light" type="button" onClick={closeOffcanvas}>
                  {t("clients.actions.cancel")}
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-save-line align-bottom me-1" />
                  )}
                  {t("clients.actions.save")}
                </Button>
              </div>
            </Form>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <ClientNotesPreviewModal
        isOpen={Boolean(notesPreview)}
        notes={notesPreview?.notes ?? ""}
        clientName={notesPreview?.clientName}
        onClose={() => setNotesPreview(null)}
      />
    </React.Fragment>
  );
};

export default ClientsList;
